import { useEffect, useCallback } from "react";
import { Button } from "@tellescope/ui";
import { Dialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { AppointmentBookingPage } from "@tellescope/types-models";
import { useOrganization } from "../../../shared/store/organizationStore";
import { buildAppointmentBookingURL } from "../../../config/portal";
import { invalidateCalendarEventQueries } from "../enduserQueryClient";

interface AppointmentBookingDialogProps {
  /** The booking page to display */
  bookingPage: AppointmentBookingPage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The iframe authentication token (fetched by parent) */
  iframeToken: string | null;
  /** Whether the token is currently loading */
  isLoading: boolean;
  /** Error message if token fetch failed */
  error: string | null;
  /** When set, this open is a reschedule of the given event rather than a fresh booking */
  rescheduledCalendarEventId?: string;
}

interface BookingSuccessMessage {
  type: "Booking Success";
  bookedEventId: string;
  entropy?: string;
}

function isBookingSuccessMessage(data: unknown): data is BookingSuccessMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as BookingSuccessMessage).type === "Booking Success" &&
    typeof (data as BookingSuccessMessage).bookedEventId === "string"
  );
}

/**
 * Dialog for booking an appointment via an embedded iframe.
 * Listens for postMessage from the iframe to detect successful bookings.
 * Token fetching is handled by the parent component (event-driven).
 */
export function AppointmentBookingDialog({
  bookingPage,
  open,
  onOpenChange,
  iframeToken,
  isLoading,
  error,
  rescheduledCalendarEventId,
}: AppointmentBookingDialogProps) {
  const { t } = useTranslation("appointments");
  const organization = useOrganization();

  // Handle booking success message from iframe
  const handleBookingSuccess = useCallback(() => {
    // Show success toast
    toast.success(
      rescheduledCalendarEventId ? t("Appointment rescheduled successfully") : t("Appointment booked successfully")
    );

    // Invalidate calendar events to refresh the data
    invalidateCalendarEventQueries();

    // Close the dialog
    onOpenChange(false);
  }, [t, onOpenChange, rescheduledCalendarEventId]);

  // Listen for postMessage from the iframe (legitimate external event sync)
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      if (isBookingSuccessMessage(event.data)) {
        handleBookingSuccess();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, handleBookingSuccess]);

  // Build the iframe URL
  // Always uses subdomain-based URL for consistent embedding (not customPortalURL)
  const bookingURL =
    bookingPage && organization && iframeToken
      ? buildAppointmentBookingURL({
          organization: { subdomain: organization.subdomain },
          bookingPageId: bookingPage.id,
          authToken: iframeToken,
          rescheduledCalendarEventId,
        })
      : null;

  const pageTitle = rescheduledCalendarEventId
    ? t("Reschedule Appointment")
    : bookingPage?.title || t("Book Appointment");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-lg h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <Dialog.Header className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => onOpenChange(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Dialog.Title className="text-base font-medium truncate pr-8">{pageTitle}</Dialog.Title>
          </div>
        </Dialog.Header>

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                <p className="text-sm">{t("Loading booking page...")}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-destructive">
                <p className="text-sm font-medium mb-1">{t("Error loading booking page")}</p>
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : bookingURL ? (
            <iframe
              src={bookingURL}
              className="w-full h-full border-0"
              title={pageTitle}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <p className="text-sm">{t("Unable to load booking page")}</p>
              </div>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
